import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { isNativeApp, showInterstitialAd } from "../lib/deviceBridge";

// Ads strategy:
//  - WEB  -> Google AdSense (client + slot from .env). AdSense is NOT allowed in apps.
//  - APP  -> native AdMob interstitial via the ParasNative bridge (see /app/mobile).
// In the native app this modal auto-triggers the native ad and closes itself.
const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT;
const ADSENSE_SLOT = process.env.REACT_APP_ADSENSE_SLOT;
const SKIP_SECONDS = 4;

export default function AdInterstitial({ open, onClose }) {
  const [left, setLeft] = useState(SKIP_SECONDS);

  useEffect(() => {
    if (!open) return;
    // Native app: show the AdMob interstitial and immediately close this web modal.
    if (isNativeApp()) {
      showInterstitialAd().finally(() => onClose && onClose());
      return;
    }
    setLeft(SKIP_SECONDS);
    const id = setInterval(() => setLeft((s) => (s <= 1 ? (clearInterval(id), 0) : s - 1)), 1000);
    if (ADSENSE_CLIENT && ADSENSE_SLOT) {
      try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch (e) { console.warn("adsense push failed", e); }
    }
    return () => clearInterval(id);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- onClose is intentionally excluded so the skip timer isn't reset by parent re-renders

  if (!open || isNativeApp()) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/90 p-4" data-testid="ad-interstitial">
      <div className="relative w-full max-w-sm rounded-2xl border border-slate-700 bg-slate-900 p-4 shadow-2xl">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Advertisement</span>
          <button
            data-testid="ad-close-button"
            onClick={onClose}
            disabled={left > 0}
            className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300 disabled:opacity-50"
          >
            {left > 0 ? `Skip in ${left}s` : <><X className="h-3 w-3" /> Close</>}
          </button>
        </div>

        {ADSENSE_CLIENT && ADSENSE_SLOT ? (
          <ins
            className="adsbygoogle block"
            style={{ display: "block", minHeight: 250 }}
            data-ad-client={ADSENSE_CLIENT}
            data-ad-slot={ADSENSE_SLOT}
            data-ad-format="auto"
            data-full-width-responsive="true"
          />
        ) : (
          <div className="flex h-64 flex-col items-center justify-center rounded-xl border border-dashed border-slate-700 bg-slate-950 text-center">
            <div className="grid h-14 w-14 place-items-center rounded-full bg-emerald-500/10 text-2xl">📣</div>
            <p className="mt-3 px-6 text-sm text-slate-400">Your ad appears here.</p>
            <p className="mt-1 px-6 text-xs text-slate-600">Add AdSense client &amp; slot in .env to show live ads.</p>
          </div>
        )}
      </div>
    </div>
  );
}
